import { useEffect, useRef } from 'react';
import { getContainer } from '@framework/di/getContainer';
import AskAiChatSubscriptionRepository, {
  type IAskAiChatHandlers,
} from '@/creditflowresearch/infrastructure/repository/AskAiChatSubscriptionRepository';

interface IParams {
  isEnabled: boolean,
  handlers: IAskAiChatHandlers,
  onBeforeUnsubscribe: () => void,
}

const useAskAiChatSubscription = ({ isEnabled, handlers, onBeforeUnsubscribe }: IParams): void => {
  const subscription = getContainer().get<AskAiChatSubscriptionRepository>(AskAiChatSubscriptionRepository);
  const handlersRef = useRef(handlers);
  const onBeforeUnsubscribeRef = useRef(onBeforeUnsubscribe);
  const attemptRef = useRef(0);

  useEffect(() => {
    handlersRef.current = handlers;
    onBeforeUnsubscribeRef.current = onBeforeUnsubscribe;
  }, [handlers, onBeforeUnsubscribe]);

  useEffect(() => {
    if (!isEnabled) {
      return undefined;
    }

    const attempt = ++attemptRef.current;
    let isCancelled = false;

    subscription.subscribe({
      onProcessing: (payload) => handlersRef.current.onProcessing(payload),
      onAnswer: (payload) => handlersRef.current.onAnswer(payload),
      onDone: (payload) => handlersRef.current.onDone(payload),
      onError: (payload) => handlersRef.current.onError(payload),
      onCancelled: (payload) => handlersRef.current.onCancelled(payload),
      onConnectionError: (message) => handlersRef.current.onConnectionError(message),
    }).then(() => {
      if (isCancelled && attempt === attemptRef.current) {
        void subscription.unsubscribe();
      }
    });

    return () => {
      isCancelled = true;
      onBeforeUnsubscribeRef.current();
      void subscription.unsubscribe();
    };
  }, [isEnabled, subscription]);
};

export default useAskAiChatSubscription;
